import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Check,
  IndianRupee,
  Layers,
  SlidersHorizontal,
  Save,
  type LucideIcon,
} from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { QueryError } from '@/components/ui/QueryError';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { flattenFieldErrors } from '@/lib/formErrors';
import { applyFieldErrors } from '@/lib/forms';
import { cn } from '@/lib/utils';

import { subjectsForClassPath } from '@/features/institute/subjects/constants';
import { classesApi } from '../api/classes.api';
import { ClassPreviewCard } from '../components/ClassPreviewCard';
import {
  ClassCurriculumSection,
  ClassDetailsSection,
  ClassFeesSection,
  ClassSectionsSection,
} from '../components/ClassSetupFields';
import { classFormResolver, classSchema, type ClassFormValues } from '../schemas/class.schema';
import { CLASSES_PALETTE, CLASSES_PANEL_SKIN } from '../theme';
import {
  DEFAULT_MEDIUM,
  EMPTY_CLASS_FORM,
  classNameOptions,
  describeFieldPath,
  isNameTaken,
  schoolMediumOptions,
  toFormValues,
} from '../utils';
import { useLiveFormErrors } from '@/hooks/useLiveFormErrors';

const CLASSES_PATH = '/admin/classes';

interface SetupStep {
  key: string;
  label: string;
  hint: string;
  icon: LucideIcon;
  /**
   * The fields this step owns.
   *
   * Two jobs: "Next" validates only these, so a fee left blank cannot stop
   * someone finishing the sections; and the rail marks a step whose fields are
   * holding up the save, which is otherwise invisible from another step.
   */
  fields: readonly (keyof ClassFormValues)[];
}

const STEPS: readonly SetupStep[] = [
  {
    key: 'details',
    label: 'Class details',
    hint: 'What it is called, who it admits, how it is filed.',
    icon: SlidersHorizontal,
    fields: [
      'name',
      'code',
      'category',
      'medium',
      'description',
      'iconKey',
      'colourKey',
      'displayOrder',
      'minAgeYears',
      'maxAgeYears',
    ],
  },
  {
    key: 'sections',
    label: 'Sections & seats',
    hint: 'How the class is split, and how many each section takes.',
    icon: Layers,
    fields: ['sections'],
  },
  {
    key: 'curriculum',
    label: 'Curriculum',
    hint: 'What this class studies, and what it may choose.',
    icon: BookOpen,
    fields: ['coreSubjects', 'electives', 'languages'],
  },
  {
    key: 'fees',
    label: 'Fees',
    hint: 'What a family pays for a full academic year.',
    icon: IndianRupee,
    fields: ['tuitionFee', 'booksFee', 'otherFees'],
  },
];

/** How full the progress bar is drawn, one class per step — no runtime widths. */
const PROGRESS_WIDTH = ['w-1/4', 'w-2/4', 'w-3/4', 'w-full'];

/**
 * Setting up a class, or correcting one that already exists.
 *
 * One screen for both, because the fields are the same set and the rules are the
 * same rules — a second component would be the one that forgot a field the first
 * had (CLAUDE.md §3). Which it is doing comes from the route: `/classes/new`, or
 * `/classes/:uuid/edit`.
 *
 * A page rather than the dialog this used to be. A class is not two fields — it
 * is a name, an age window, up to ten sections with their own intakes, three
 * curriculum lists and a fee — and a dialog asks all of that through a letterbox
 * with the roster greyed out behind it. On a page it can be asked a step at a
 * time, with the class being built drawn beside the questions.
 *
 * The roster is loaded even when adding, for two reasons that both need it: the
 * session this class will belong to, and the names already taken.
 */
export function ClassSetupPage() {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();
  const isEdit = Boolean(uuid);

  const permissions = useAuthStore((state) => state.user?.permissions);
  const canManage = Boolean(permissions?.includes('CLASS_MANAGE'));

  const { data: classes, isLoading, error, refetch } = useApiQuery(() => classesApi.list(), []);
  // The subject master and the school's own medium. Not essential: if it fails,
  // the form still works by typing, and a new class defaults to English.
  const { data: setupOptions, refetch: refetchSetupOptions } = useApiQuery(
    () => classesApi.setupOptions(),
    [],
  );

  const [stepIndex, setStepIndex] = useState(0);
  const [formError, setFormError] = useState<string | null>(null);
  /** Where a Leave waiting on "Leave without saving?" goes once confirmed. */
  const [leavingTo, setLeavingTo] = useState<string | null>(null);
  /** A class just created with no subjects, offered the step to them before the list. */
  const [created, setCreated] = useState<{ uuid: string; name: string } | null>(null);

  const form = useForm<ClassFormValues>({
    resolver: classFormResolver,
    defaultValues: EMPTY_CLASS_FORM,
    mode: 'onChange',
  });
  useLiveFormErrors(form);
  const {
    formState: { errors, isSubmitting, isDirty, submitCount },
  } = form;

  const instituteClass = uuid ? (classes?.find((row) => row.uuid === uuid) ?? null) : null;
  const academicYear = classes?.[0]?.academicYear.name;

  // Fill the form once the class it is editing has arrived. Adding starts on
  // `EMPTY_CLASS_FORM` and is left alone.
  useEffect(() => {
    if (!instituteClass) return;
    form.reset(toFormValues(instituteClass));
  }, [instituteClass, form]);

  /*
    A new class takes the school's own medium rather than none. A class saved
    with no medium follows the institute record, and where that record names
    none the class matched no medium filter at all. Stated on the class, it is
    found by the filter whatever the institute record says later. Only while
    the field is untouched — never over a choice already made.
  */
  useEffect(() => {
    if (isEdit || !setupOptions || form.getFieldState('medium').isDirty) return;
    form.setValue('medium', setupOptions.institute.mediums[0] ?? DEFAULT_MEDIUM);
  }, [isEdit, setupOptions, form]);

  useEffect(() => {
    const subscription = form.watch(() => {
      if (formError) setFormError(null);
    });
    return () => subscription.unsubscribe();
  }, [form, formError]);

  /**
   * A name another class already answers to.
   *
   * Checked as it is typed rather than waiting for the server to refuse the
   * save — and it is a warning, not a block: the server holds the real
   * constraint, and it knows about classes this roster may not (a suspended one,
   * for instance).
   */
  const typedName = form.watch('name') ?? '';
  const takenNames = (classes ?? [])
    .filter((row) => row.uuid !== uuid)
    .map((row) => row.name);
  const isDuplicateName = typedName.trim().length > 0 && isNameTaken(takenNames, typedName);
  const nameOptions = classNameOptions(instituteClass?.name, setupOptions?.classRange);
  const mediumOptions = schoolMediumOptions(setupOptions?.institute.mediums, instituteClass?.medium ?? undefined);

  /**
   * What is stopping the save, once one has been attempted.
   *
   * The field holding it up is usually on another step when the button is
   * pressed, so a red outline nobody can see reads as "Save does nothing".
   * Recomputed each render, so entries disappear as they are fixed.
   */
  const blocking = submitCount > 0 ? flattenFieldErrors(errors) : [];

  const step = STEPS[stepIndex];
  const isLastStep = stepIndex === STEPS.length - 1;

  async function handleStepClick(targetIndex: number) {
    if (targetIndex === stepIndex) return;

    if (targetIndex < stepIndex) {
      setStepIndex(targetIndex);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    for (let i = 0; i < targetIndex; i++) {
      const isStepValid = await form.trigger(STEPS[i].fields);
      if (!isStepValid) {
        setStepIndex(i);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }

    setStepIndex(targetIndex);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function goToNextStep() {
    const isStepValid = await form.trigger(step.fields);
    if (!isStepValid) return;
    setStepIndex((index) => Math.min(index + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /** Leaves for `path`, asking first when the form holds unsaved changes. */
  function leaveTo(path: string) {
    if (isDirty) setLeavingTo(path);
    else navigate(path);
  }

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    const payload = classSchema.parse(values);

    try {
      const saved = instituteClass
        ? await classesApi.update(instituteClass.uuid, payload)
        : await classesApi.create(payload);

      if (instituteClass) {
        toast.success(`${saved.name} has been updated`, `${saved.totalCapacity} seats in total.`);
        navigate(CLASSES_PATH);
      } else {
        toast.success(
          `${saved.name} is set up`,
          `${saved.totalCapacity} seats across ${saved.sections.length} ${saved.sections.length === 1 ? 'section' : 'sections'}.`,
        );
        // The Curriculum step saves its subjects with the class, so asking to
        // assign them again only makes sense when that step was left empty.
        const hasSubjects =
          payload.coreSubjects.length + payload.electives.length + payload.languages.length > 0;
        if (hasSubjects) navigate(CLASSES_PATH);
        else setCreated({ uuid: saved.uuid, name: saved.name });
      }
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        applyFieldErrors(form.setError, caught);
        setFormError(caught.message);
        return;
      }
      setFormError('Something went wrong. Please try again.');
    }
  });

  if (error) {
    return (
      <div className={CLASSES_PALETTE}>
        <QueryError title="Could not load your classes" error={error} onRetry={refetch} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={cn('flex flex-col gap-5', CLASSES_PALETTE)}>
        <div className="rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-[#111c2e] p-5 shadow-[0_12px_32px_-8px_rgba(15,23,42,0.12)]">
          <SkeletonLines count={6} />
        </div>
      </div>
    );
  }

  // An edit link that no longer points at anything — a class suspended in
  // another tab, or a pasted URL. Said plainly rather than shown as an empty
  // form that would silently create a second class on save.
  if (isEdit && !instituteClass) {
    return (
      <div className={cn('flex flex-col gap-4', CLASSES_PALETTE)}>
        <Alert tone="critical" title="That class is no longer on the roster">
          It may have been suspended since this link was opened.
        </Alert>
        <Link
          to={CLASSES_PATH}
          className="inline-flex w-fit items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-sky-600 px-6 py-2.5 text-sm font-black text-white shadow-lg shadow-blue-500/30 hover:brightness-105 active:brightness-95 transition-[filter,transform,box-shadow] duration-150"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Back to classes
        </Link>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-6 animate-hero-zoom', CLASSES_PALETTE)}>
      {/* Top Breadcrumb & Page Header */}
      <header className="min-w-0">
        <div className="flex items-center gap-2 text-sm font-medium mb-2">
          <button
            type="button"
            onClick={() => leaveTo(CLASSES_PATH)}
            className="inline-flex items-center gap-1.5 text-slate-600 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-400 transition-colors cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Classes</span>
          </button>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:gap-3">
          <h1 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight shrink-0">
            {instituteClass ? `Edit ${instituteClass.name}` : 'Add class'} 
          </h1>
          <p className=" mt-1 text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400">
           <span className='pe-2'> - </span> {instituteClass
              ? `Changes apply to ${instituteClass.academicYear.name} only. Earlier years keep what they were set up with.`
              : 'A saved class becomes selectable on admission forms and fee plans. Nothing is written until you save.'}
          </p>
        </div>
      </header>

      {!canManage && (
        <Alert tone="info" title="Read only">
          Your role lets you see the class roster but not change it. Ask your institute
          administrator to set this class up.
        </Alert>
      )}

      {/* The rail: three steps, a bar that fills as they are cleared, and a dot
          on any step whose fields are holding up the save. */}
      <div className="rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-[#111c2e] p-3 shadow-sm animate-slide-down delay-50">
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
          <div
            className={cn(
              'h-full rounded-full bg-indigo-600 transition-all duration-500',
              PROGRESS_WIDTH[stepIndex],
            )}
            aria-hidden
          />
        </div>

        <div
          role="tablist"
          aria-label="Class setup steps"
          className="mt-3 flex gap-2 overflow-x-auto pb-1 md:grid md:grid-cols-4 md:overflow-visible md:pb-0"
        >
          {STEPS.map((item, index) => {
            const Icon = item.icon;
            const isActive = index === stepIndex;
            const isDone = index < stepIndex;
            const hasErrors = item.fields.some((field) => errors[field]);

            return (
              <button
                key={item.key}
                role="tab"
                type="button"
                aria-selected={isActive}
                onClick={() => void handleStepClick(index)}
                className={cn(
                  'flex shrink-0 cursor-pointer items-start gap-2.5 sm:gap-3 rounded-xl sm:rounded-2xl border p-2.5 sm:p-3 text-left transition-colors duration-150 whitespace-nowrap md:whitespace-normal md:w-auto',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50',
                  isActive
                    ? 'border-indigo-200 dark:border-indigo-800/70 bg-indigo-50/70 dark:bg-indigo-950/40 shadow-xs'
                    : 'border-transparent hover:border-slate-200 dark:hover:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900/50',
                )}
              >
                <span className="relative shrink-0" aria-hidden>
                  <span
                    className={cn(
                      'relative grid h-7 w-7 sm:h-9 sm:w-9 place-items-center overflow-hidden rounded-lg sm:rounded-xl border transition-colors duration-150',
                      isActive
                        ? 'border-indigo-500/30 bg-indigo-600 text-white shadow-md shadow-indigo-500/25'
                        : isDone
                          ? 'border-indigo-200 dark:border-indigo-900/60 bg-indigo-100 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300'
                          : 'border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400',
                    )}
                  >
                    {isDone ? (
                      <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4" strokeWidth={3} />
                    ) : (
                      <Icon className="relative h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    )}
                  </span>
                  {hasErrors && (
                    <span className="absolute -right-1 -top-1 h-2 sm:h-2.5 w-2 sm:w-2.5 rounded-full border-2 border-white dark:border-[#111c2e] bg-rose-500" />
                  )}
                </span>

                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline gap-2">
                    <span
                      className={cn(
                        'text-[13px] sm:text-[14px] font-extrabold whitespace-nowrap',
                        isActive
                          ? 'text-indigo-700 dark:text-indigo-300'
                          : 'text-slate-700 dark:text-slate-300',
                      )}
                    >
                      {item.label}
                    </span>
                    <span className="ml-auto shrink-0 font-mono text-[11px] sm:text-[12px] font-bold text-slate-400 dark:text-slate-600">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </span>
                  <span className="mt-1 hidden text-[11px] font-medium leading-snug text-slate-500 dark:text-slate-400 md:block">
                    {item.hint}
                  </span>
                  {hasErrors && (
                    <span className="mt-1 hidden text-[11px] font-bold text-rose-600 dark:text-rose-400 md:block">
                      Needs attention
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12 animate-slide-down delay-100 pb-8">
        <div className="flex flex-col gap-4 lg:col-span-7 xl:col-span-8">
          {formError && (
            <Alert tone="critical" title="Could not save this class">
              {formError}
            </Alert>
          )}

          {blocking.length > 0 && (
            <Alert tone="warning" title="Fix these before saving">
              <ul className="mt-1 flex flex-col gap-0.5">
                {blocking.map((issue) => (
                  <li key={issue.path}>
                    <span className="font-[560]">{describeFieldPath(issue.path)}</span> —{' '}
                    {issue.message}
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          {isDuplicateName && (
            <Alert tone="warning" title="Another class already has that name">
              Your institute already runs a class called “{typedName.trim()}”. Two with the same name
              are hard to tell apart on admission forms and fee plans.
            </Alert>
          )}

          <form
            onSubmit={onSubmit}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT' && !isLastStep) {
                e.preventDefault();
              }
            }}
            noValidate
            className="flex flex-col gap-5"
          >
            <fieldset disabled={!canManage || isSubmitting} className="contents">
              <div className={cn('flex flex-col gap-5 pb-6', CLASSES_PANEL_SKIN)}>
                {/* Every step stays mounted: a field array unmounted halfway
                    through loses its rows, and an error on a step nobody is
                    standing on still has to be reachable. */}
                <div className={stepIndex === 0 ? 'block animate-tab-fade' : 'hidden'}>
                  <ClassDetailsSection
                    form={form}
                    nameOptions={nameOptions}
                    schoolBoard={setupOptions?.institute.board ?? null}
                    mediumOptions={mediumOptions}
                  />
                </div>
                <div className={stepIndex === 1 ? 'block animate-tab-fade' : 'hidden'}>
                  <ClassSectionsSection form={form} />
                </div>
                <div className={stepIndex === 2 ? 'block animate-tab-fade' : 'hidden'}>
                  <ClassCurriculumSection
                    form={form}
                    subjects={setupOptions?.subjects}
                    onSubjectsChanged={refetchSetupOptions}
                    onManageClassSubjects={
                      instituteClass
                        ? () => leaveTo(subjectsForClassPath(instituteClass.uuid))
                        : undefined
                    }
                  />
                </div>
                <div className={stepIndex === 3 ? 'block animate-tab-fade' : 'hidden'}>
                  <ClassFeesSection form={form} />
                </div>
              </div>
            </fieldset>

            {/* The bar rides the bottom of the viewport with a solid background so content never peeks underneath */}
            <div className="sticky bottom-0 z-20 -mx-4 -mb-4 px-4 sm:px-6 py-3.5 sm:py-4 bg-white/95 dark:bg-[#111c2e]/95 border-t border-slate-200 dark:border-slate-800 backdrop-blur-md">
              <div className="flex flex-wrap items-center justify-between gap-3 sm:gap-4">
                <button
                  type="button"
                  onClick={() =>
                    stepIndex === 0 ? leaveTo(CLASSES_PATH) : setStepIndex((index) => index - 1)
                  }
                  className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800/90 hover:bg-slate-200 dark:hover:bg-slate-700 px-5 py-2.5 text-sm font-bold text-slate-700 dark:text-slate-200 shadow-xs transition-all w-full sm:w-auto"
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden />
                  {stepIndex === 0 ? 'Cancel' : 'Back'}
                </button>

                {canManage &&
                  (isLastStep ? (
                    <button
                      key="btn-save-class"
                      type="submit"
                      disabled={isSubmitting}
                      className="inline-flex cursor-pointer items-center justify-center gap-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 px-6 py-2.5 text-sm font-black text-white shadow-md shadow-indigo-600/25 transition-all disabled:cursor-not-allowed disabled:opacity-50 w-full sm:w-auto"
                    >
                      {isSubmitting ? (
                        <span>Saving…</span>
                      ) : (
                        <>
                          <Save className="h-4 w-4 stroke-[2.5]" aria-hidden />
                          <span>{instituteClass ? 'Save changes' : 'Save class'}</span>
                        </>
                      )}
                    </button>
                  ) : (
                    <button
                      key="btn-next-step"
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        void goToNextStep();
                      }}
                      className="inline-flex cursor-pointer items-center justify-center gap-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 px-6 py-2.5 text-sm font-black text-white shadow-md shadow-indigo-600/25 transition-all w-full sm:w-auto"
                    >
                      <span>Next — {STEPS[stepIndex + 1].label}</span>
                      <ArrowRight className="h-4 w-4 stroke-[2.5]" aria-hidden />
                    </button>
                  ))}
              </div>
            </div>
          </form>
        </div>

        {/* The class as it is being typed. Sticky, because it is the answer to
            "what am I actually building" and that question is asked on every
            step. */}
        <aside className="lg:col-span-5 xl:col-span-4">
          <div className="lg:sticky lg:top-6">
            <ClassPreviewCard form={form} academicYear={academicYear} />
          </div>
        </aside>
      </div>

      <ConfirmDialog
        isOpen={leavingTo !== null}
        title="Leave without saving?"
        description="This class has not been saved. What you have typed here will be lost."
        confirmLabel="Discard and leave"
        tone="danger"
        onConfirm={() => navigate(leavingTo ?? CLASSES_PATH)}
        onCancel={() => setLeavingTo(null)}
      />

      <ConfirmDialog
        isOpen={created !== null}
        title={`${created?.name ?? 'This class'} is set up. Assign its subjects now?`}
        description="Pick the core subjects, electives and languages this class teaches this year. You can also do it later from the Subjects page."
        confirmLabel="Assign subjects"
        cancelLabel="Later"
        onConfirm={() => created && navigate(subjectsForClassPath(created.uuid))}
        onCancel={() => navigate(CLASSES_PATH)}
      />
    </div>
  );
}
